import constants from '@/shared/constants/constants';

/**
 * Segment bar colors share a single source of truth with the survey result graph:
 * the `--color-segment-*` CSS variables declared in common/bootstrap.css (and consumed
 * by the `.segAll`, `.age20`, … classes in survey_pc.css). We only map each segment
 * class to its variable name here, so updating a color in one place keeps survey and
 * concept valuation bars in sync (no hardcoded hex to drift).
 */
const SEGMENT_CLASS_COLOR_VARS = {
  segAll: 'var(--color-segment-all)',
  segMale: 'var(--color-segment-male)',
  segFemale: 'var(--color-segment-female)',
  segCate: 'var(--color-segment-category)',
  age20: 'var(--color-segment-age20)',
  age30: 'var(--color-segment-age30)',
  age40: 'var(--color-segment-age40)',
  age50: 'var(--color-segment-age50)',
  male20: 'var(--color-segment-male20)',
  male30: 'var(--color-segment-male30)',
  male40: 'var(--color-segment-male40)',
  male50: 'var(--color-segment-male50)',
  female20: 'var(--color-segment-female20)',
  female30: 'var(--color-segment-female30)',
  female40: 'var(--color-segment-female40)',
  female50: 'var(--color-segment-female50)',
};

const CV_SEGMENT_DEFAULT_COLOR = '#77bae1';

export function conceptValuationMenuSegmentIds(detail, menuId) {
  const data = detail || {};
  const menu = Number(menuId);
  let raw;
  if (menu === constants.MENU_FOR_CONCEPT_VALUATION_ATTRACTIVE) {
    raw = data.attractive_segment_types;
  } else if (menu === constants.MENU_FOR_CONCEPT_VALUATION_USAGE) {
    raw = data.usage_segment_types;
  }
  if (!Array.isArray(raw)) {
    raw = data.segment_types || [];
  }
  return [...new Set(
    raw.map(id => Number(id)).filter(id => !Number.isNaN(id)),
  )];
}

/** Precomputed segment id → bar color map (built once). */
const SEGMENT_ID_COLORS = [
  ...(constants.segmentTypeDefault || []),
  ...(constants.segmentTypeAge || []),
  ...(constants.segmentMix || []),
].reduce((map, seg) => {
  if (seg && seg.id != null) {
    map[Number(seg.id)] = SEGMENT_CLASS_COLOR_VARS[seg.class] || CV_SEGMENT_DEFAULT_COLOR;
  }
  return map;
}, {});

function flattenSegmentListIds() {
  const groups = constants.SEGMENT_LIST || [];
  const out = [];
  groups.forEach((row) => {
    (row || []).forEach((seg) => {
      if (seg && seg.id != null) out.push(Number(seg.id));
    });
  });
  return out;
}

export default {
  computed: {
    conceptValuationSegmentIdOrder() {
      return flattenSegmentListIds();
    },
    segmentIdsForDetail() {
      const detail = this.detail || {};
      const types = (detail.segment_types || []).map(t => Number(t)).filter(v => !Number.isNaN(v));
      return this.sortConceptValuationSegmentIds(
        types.length ? types : [constants.SEGMENT_OVERALL],
      );
    },
  },
  methods: {
    conceptValuationSegmentIdsForMenu(menuId) {
      return this.sortConceptValuationSegmentIds(
        conceptValuationMenuSegmentIds(this.detail, menuId),
      );
    },
    sortConceptValuationSegmentIds(ids) {
      const order = this.conceptValuationSegmentIdOrder;
      return [...ids].sort((a, b) => {
        const ia = order.indexOf(Number(a));
        const ib = order.indexOf(Number(b));
        if (ia === -1 && ib === -1) {
          return Number(a) - Number(b);
        }
        if (ia === -1) {
          return 1;
        }
        if (ib === -1) {
          return -1;
        }
        return ia - ib;
      });
    },
    segmentColor(segmentId) {
      return SEGMENT_ID_COLORS[Number(segmentId)] || CV_SEGMENT_DEFAULT_COLOR;
    },
    segmentLabel(segmentId) {
      const id = Number(segmentId);
      const lang = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'ja';
      const segDefault = (constants.segmentTypeDefault || []).find(s => Number(s.id) === id);
      if (segDefault) {
        return (segDefault.textLang && segDefault.textLang[lang]) || segDefault.textDisplay || String(id);
      }
      const segAge = (constants.segmentTypeAge || []).find(s => Number(s.id) === id);
      if (segAge) {
        return (segAge.textLang && segAge.textLang[lang]) || segAge.textDisplay || String(id);
      }
      const segMix = (constants.segmentMix || []).find(s => Number(s.id) === id);
      if (segMix) {
        return (segMix.textLang && segMix.textLang[lang]) || segMix.textDisplay || String(id);
      }
      return String(id);
    },
    /** CSV/UI title for average segment mode: Total (seg1, seg2, …) — aligned with survey export. */
    conceptValuationCombinationTitle() {
      const detail = this.detail || {};
      const segmentIds = this.sortConceptValuationSegmentIds(
        (detail.segment_types || []).map(t => Number(t)).filter(id => !Number.isNaN(id)),
      );
      const names = segmentIds.map(id => this.segmentLabel(id)).filter(Boolean);
      const segmentTitlesStr = names.length > 0 ? ` (${names.join(', ')})` : '';
      return `${this.$t('concept_valuation.combination')}${segmentTitlesStr}`;
    },
  },
};
